import { useProjectParams, useContents, useCollectionKey, useManualTags } from "hooks";
import type { FunctionComponent } from "react";
import React, { useContext, useMemo } from "react";
import { AiOutlineFileText } from "react-icons/ai";
import { SmallActionButton } from "screens/content/contentCanvas/cell/SmallActionButton";
import { onOpenContent } from "screens/content/contentView/previewSection/ContentPreview";
import { useAddToCharliContext } from "screens/panels/addToCharli/AddToCharliWizard/AddToCharliProvider";
import { ConversationContext } from "screens/thread/ConversationContext";
import { hasProjectOutput } from "./utils";
import type { ContentDetails } from "types/content/ContentDetails";

interface Props {
  collectionId: string;
  isDisabled?: boolean;
  showFirstReport?: boolean;
  size?: string;
}

export const ProjectReportPanelButton: FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({
  collectionId,
  isDisabled,
  showFirstReport = false,
  size = ".9rem",
}) => {
  const { onProjectReportPanelOpen } = useAddToCharliContext();
  const { projectId } = useProjectParams();
  const collectionMetadataIds = useCollectionKey(collectionId, "metadataIds");
  const tagsFilter = useManualTags();
  const contentData = useContents(collectionMetadataIds ?? [], { refreshFromNetwork: false, filterByTags: tagsFilter });
  const hasReports = useMemo(() => {
    return contentData.length > 0;
  }, [contentData]);
  const { setSelectedContentId } = useContext(ConversationContext);

  const contentDataFiltered = useMemo(() => {
    const filtered = contentData.filter((content) => {
      return hasProjectOutput(content);
    });
    const sorted = filtered?.sort((a: ContentDetails, b: ContentDetails) => {
      return new Date(b.timeCreated).getTime() - new Date(a.timeCreated).getTime();
    });
    return sorted;
  }, [contentData]);

  return (
    <SmallActionButton
      isDisabled={isDisabled || !hasReports}
      classNames={"ch-open-report-panel"}
      iconTypeName={AiOutlineFileText}
      tooltip={isDisabled ? "" : !hasReports ? "No reports created yet" : showFirstReport ? "View Report" : "Open Report Panel"}
      iconSize={size}
      pt="1px"
      onClick={() => {
        if (showFirstReport && contentDataFiltered) {
          onOpenContent(contentDataFiltered[0]);
          return;
        }
        setSelectedContentId(collectionId || projectId);
        onProjectReportPanelOpen();
      }}
    />
  );
};
